import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  HStack,
  Input,
  Select,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
} from "@chakra-ui/react";
import { ArrowBackIcon, EditIcon } from "@chakra-ui/icons";
import { FaUserClock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { getConsultantTimeline } from "../../../api/consultantsApi";
import { updateMatch } from "../../../api/matchesApi";
import {
  MATCH_ASSIGNMENT_STATUSES,
  MATCH_ASSIGNMENT_STATUS_LABELS,
  MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
  MATCH_PURCHASE_ORDER_STATUSES,
  MATCH_PURCHASE_ORDER_STATUS_LABELS,
  MATCH_PURCHASE_ORDER_STATUS_LABEL_KEYS,
  ASSIGNMENT_STATUS_COLORS,
  PURCHASE_ORDER_STATUS_COLORS,
  getStatusLabel,
} from "../../../constants/statuses";
import {
  PageHeader,
  Panel,
  KpiStrip,
  KpiCard,
  StatusBadge,
  MiniIconButton,
  EmptyState,
  FieldLabel,
} from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";
import ClientDetailModal from "./ClientDetailModal";
import NeedDetailModal from "./NeedDetailModal";

// Token control sizing (31px) shared across migrated portal forms/filters.
const TOKEN_INPUT_SX = {
  height: "31px !important",
  fontWeight: 600,
  bg: "white",
};

const TOKEN_SELECT_SX = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  fontSize: "12px",
  fontWeight: 600,
};

const toDateInputValue = (d) => {
  if (!d) return "";
  try {
    if (typeof d === "string") {
      const trimmed = d.trim();
      if (!trimmed) return "";
      // Handle dd/mm/yyyy (optionally with time)
      const match = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[,\s].*)?$/);
      if (match) {
        const [, dd, mm, yyyy] = match;
        return `${yyyy}-${mm}-${dd}`;
      }
      // Already yyyy-mm-dd
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    }

    const dt = typeof d === "string" ? new Date(d) : d;
    if (!(dt instanceof Date) || Number.isNaN(dt.getTime())) return "";
    return dt.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const defaultRange = () => {
  const today = new Date();
  const from = new Date(today);
  from.setMonth(from.getMonth() - 6);
  const to = new Date(today);
  to.setMonth(to.getMonth() + 6);
  return { fromDate: toDateInputValue(from), toDate: toDateInputValue(to) };
};

const ConsultantTrackingDetail = ({ consultantId: consultantIdProp, showBackButton = true, onBack }) => {
  const { consultantId: consultantIdParam } = useParams();
  const consultantId = consultantIdProp || consultantIdParam;
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  const [range, setRange] = useState(() => defaultRange());
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState(null);

  const trackingModal = useDisclosure();
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [saving, setSaving] = useState(false);

  // Cross-entity modals
  const clientDetailModal = useDisclosure();
  const needDetailModal = useDisclosure();
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedNeedId, setSelectedNeedId] = useState(null);

  const [form, setForm] = useState({
    assignmentStatus: "",
    assignmentStartDate: "",
    assignmentEndDate: "",
    purchaseOrderStatus: "",
    purchaseOrderNumber: "",
    purchaseOrderReceivedAt: "",
    purchaseOrderEndDate: "",
    clientSatisfactionScore: "",
    clientSatisfactionComment: "",
  });

  const fetchTimeline = useCallback(async () => {
    if (!consultantId) return;
    if (!isBUContextLoaded) return;

    setLoading(true);
    try {
      const params = {
        fromDate: range.fromDate || undefined,
        toDate: range.toDate || undefined,
      };
      const data = await getConsultantTimeline(consultantId, params, {
        buId: selectedBusinessUnitId || undefined,
      });
      setTimeline(data || null);
    } catch {
      setTimeline(null);
    } finally {
      setLoading(false);
    }
  }, [consultantId, isBUContextLoaded, range.fromDate, range.toDate, selectedBusinessUnitId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  const consultant = timeline?.consultant || null;
  const rangeStart = timeline?.rangeStart || null;

  const months = useMemo(() => {
    return Array.isArray(timeline?.months) ? timeline.months : [];
  }, [timeline]);

  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);

  // Keep selected month in range when data changes
  useEffect(() => {
    if (!months || months.length === 0) {
      setSelectedMonthIndex(0);
      return;
    }
    setSelectedMonthIndex((idx) => Math.min(Math.max(0, idx), months.length - 1));
  }, [months]);

  const selectedMonth = months[selectedMonthIndex] || null;

  const openTrackingModal = (assignment) => {
    setEditingAssignment(assignment);
    setForm({
      assignmentStatus: assignment?.status || assignment?.assignment_status || "",
      assignmentStartDate: toDateInputValue(
        assignment?.start ||
          assignment?.startDate ||
          assignment?.assignment_start_date
      ),
      assignmentEndDate: toDateInputValue(
        assignment?.end ||
          assignment?.endDate ||
          assignment?.assignment_end_date
      ),
      purchaseOrderStatus: assignment?.purchaseOrderStatus || assignment?.purchase_order_status || "",
      purchaseOrderNumber: assignment?.purchaseOrderNumber || assignment?.purchase_order_number || "",
      purchaseOrderReceivedAt:
        assignment?.purchaseOrderReceivedAt ||
        assignment?.purchase_order_received_at ||
        "",
      purchaseOrderEndDate: toDateInputValue(
        assignment?.purchaseOrderEndDate ||
          assignment?.purchase_order_end_date
      ),
      clientSatisfactionScore:
        assignment?.clientSatisfactionScore ?? assignment?.client_satisfaction_score ?? "",
      clientSatisfactionComment:
        assignment?.clientSatisfactionComment || assignment?.client_satisfaction_comment || "",
    });
    trackingModal.onOpen();
  };

  const closeTrackingModal = () => {
    trackingModal.onClose();
    setEditingAssignment(null);
  };

  const handleSaveTracking = async () => {
    if (!editingAssignment?.matchId && !editingAssignment?.match_id) return;
    const matchId = editingAssignment.matchId || editingAssignment.match_id;

    setSaving(true);
    try {
      const payload = {
        assignmentStatus: form.assignmentStatus || null,
        assignmentStartDate: form.assignmentStartDate || null,
        assignmentEndDate: form.assignmentEndDate || null,
        purchaseOrderStatus: form.purchaseOrderStatus || null,
        purchaseOrderNumber: form.purchaseOrderNumber || null,
        purchaseOrderReceivedAt: form.purchaseOrderReceivedAt || null,
        purchaseOrderEndDate: form.purchaseOrderEndDate || null,
        clientSatisfactionScore:
          form.clientSatisfactionScore === "" ? null : Number(form.clientSatisfactionScore),
        clientSatisfactionComment: form.clientSatisfactionComment || null,
      };

      await updateMatch(matchId, payload, {
        buId: selectedBusinessUnitId || undefined,
      });

      toast({
        title: t("pilotConsultantTracking.detail.toasts.updateMatchSuccess"),
        status: "success",
      });

      closeTrackingModal();
      fetchTimeline();
    } catch (e) {
      toast({
        title: t("pilotConsultantTracking.detail.toasts.updateMatchFailed"),
        description: e?.response?.data?.message || undefined,
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const formatMonthLabel = useCallback(
    (raw, idx) => {
      const tryParseDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
        if (typeof value !== "string") return null;
        const trimmed = value.trim();
        if (!trimmed) return null;
        if (/^\d{4}-\d{2}$/.test(trimmed)) {
          const dt = new Date(`${trimmed}-01`);
          return Number.isNaN(dt.getTime()) ? null : dt;
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
          const dt = new Date(trimmed);
          return Number.isNaN(dt.getTime()) ? null : dt;
        }
        const dt = new Date(trimmed);
        return Number.isNaN(dt.getTime()) ? null : dt;
      };

      const addMonths = (date, monthsToAdd) => {
        const dt = new Date(date);
        dt.setMonth(dt.getMonth() + monthsToAdd);
        return Number.isNaN(dt.getTime()) ? null : dt;
      };

      const parsed =
        tryParseDate(raw) ||
        (rangeStart ? addMonths(new Date(rangeStart), idx) : null);

      if (parsed) {
        return new Intl.DateTimeFormat(undefined, {
          month: "short",
          year: "numeric",
        }).format(parsed);
      }

      return raw != null && String(raw).trim().length ? String(raw) : `${idx + 1}`;
    },
    [rangeStart]
  );

  const chartData = useMemo(() => {
    const arr = (months || []).map((m, idx) => {
      const rawLabel = m.label || m.monthLabel || m.month || m.key || null;
      const label = formatMonthLabel(rawLabel, idx);
      const assignments = Array.isArray(m.assignments) ? m.assignments : [];
      const isAvailable = m.isAvailable ?? m.is_available;
      const hasSalaryReview = m.hasSalaryReview ?? m.has_salary_review;
      const salaryReviewDay = m.salaryReviewDay ?? m.salary_review_day;
      return {
        idx,
        label,
        assignments,
        value: assignments.length,
        isAvailable,
        hasSalaryReview,
        salaryReviewDay,
      };
    });
    return arr;
  }, [months, formatMonthLabel]);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate("/portal/asap-pilot/consultant-tracking");
  };

  const handleViewConsultant = () => {
    if (!consultantId) return;
    navigate(`/portal/asap-pilot/consultants/${consultantId}`);
  };

  const consultantName =
    consultant?.name ||
    consultant?.fullName ||
    consultant?.full_name ||
    t("pilotConsultantTracking.detail.titleFallback");

  const isAvailable = consultant?.isAvailable ?? consultant?.is_available;
  const hasNextConfirmed =
    consultant?.hasNextConfirmedAssignment ?? consultant?.has_next_confirmed_assignment;
  const hasSalaryReview = consultant?.hasSalaryReview ?? consultant?.has_salary_review;
  const salaryReviewDay = consultant?.salaryReviewDay ?? consultant?.salary_review_day;

  const selectedAssignments = Array.isArray(selectedMonth?.assignments)
    ? selectedMonth.assignments
    : [];

  // Header status badge: availability state, dot-prefixed.
  const headerStatusBadge =
    timeline && consultant ? (
      <StatusBadge
        label={
          isAvailable
            ? t("pilotConsultantTracking.list.badges.available")
            : t("pilotConsultantTracking.list.badges.onMission")
        }
        colorScheme={isAvailable ? "green" : "red"}
      />
    ) : null;

  const headerActions = (
    <HStack spacing="8px">
      {showBackButton && (
        <Button
          variant="secondary"
          size="sm"
          h="32px"
          px="12px"
          fontSize="12px"
          leftIcon={<ArrowBackIcon />}
          onClick={handleBack}
          aria-label={t("pilotConsultantTracking.detail.backAria")}
        >
          {t("pilotConsultantTracking.detail.actions.back", "Back")}
        </Button>
      )}
      <Button
        variant="primary"
        size="sm"
        h="32px"
        px="12px"
        fontSize="12px"
        onClick={handleViewConsultant}
        isDisabled={!consultantId}
      >
        {t("pilotConsultantTracking.detail.actions.viewConsultant", {
          defaultValue: "View consultant",
        })}
      </Button>
    </HStack>
  );

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%" display="flex" flexDirection="column">
      <PageHeader
        moduleCode="PI"
        eyebrow={t("pilotConsultantTracking.list.eyebrow", "ASAP Pilot")}
        title={
          <Flex align="center" gap="10px" flexWrap="wrap">
            <Box as="span" isTruncated>
              {consultantName}
            </Box>
            {headerStatusBadge}
          </Flex>
        }
        actions={headerActions}
      />

      <Box p="20px" flex="1" minH="0" display="flex" flexDirection="column" gap="16px">
        {/* KPI strip — consultant status figures */}
        <KpiStrip>
          <KpiCard
            label={t("pilotConsultantTracking.detail.kpis.availability", "Availability")}
            value={
              isAvailable
                ? t("pilotConsultantTracking.list.badges.available")
                : t("pilotConsultantTracking.list.badges.onMission")
            }
            trend={isAvailable ? t("pilotConsultantTracking.list.badges.available") : undefined}
            trendColor={isAvailable ? "#0F766E" : undefined}
          />
          <KpiCard
            label={t("pilotConsultantTracking.detail.kpis.nextAssignment", "Next assignment")}
            value={
              hasNextConfirmed
                ? t("pilotConsultantTracking.list.badges.nextConfirmed")
                : t("pilotConsultantTracking.list.badges.noNextConfirmed")
            }
          />
          <KpiCard
            label={t("pilotConsultantTracking.detail.kpis.salaryReview", "Salary review")}
            value={
              hasSalaryReview
                ? salaryReviewDay
                  ? t("pilotConsultantTracking.list.badges.salaryReviewDay", { day: salaryReviewDay })
                  : t("pilotConsultantTracking.list.badges.salaryReviewThisMonth")
                : t("pilotConsultantTracking.list.badges.noSalaryReviewThisMonth")
            }
          />
          <KpiCard
            label={t("pilotConsultantTracking.detail.kpis.months", "Months tracked")}
            value={loading ? "…" : String(months.length)}
          />
        </KpiStrip>

        {/* Date-range filter panel */}
        <Panel
          title={t("pilotConsultantTracking.detail.rangePanelTitle", "Tracking range")}
          filters={
            <Flex gap="12px" flexWrap="wrap" align="flex-end" w="100%">
              <FormControl maxW="200px">
                <FieldLabel>{t("pilotConsultantTracking.detail.filters.fromDate")}</FieldLabel>
                <Input
                  type="date"
                  value={range.fromDate}
                  onChange={(e) => setRange((r) => ({ ...r, fromDate: e.target.value }))}
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl maxW="200px">
                <FieldLabel>{t("pilotConsultantTracking.detail.filters.toDate")}</FieldLabel>
                <Input
                  type="date"
                  value={range.toDate}
                  onChange={(e) => setRange((r) => ({ ...r, toDate: e.target.value }))}
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <Button
                variant="primary"
                size="sm"
                h="31px"
                px="14px"
                fontSize="12px"
                onClick={fetchTimeline}
                isLoading={loading}
              >
                {t("pilotConsultantTracking.detail.filters.refresh")}
              </Button>
            </Flex>
          }
          bodyProps={{ p: "12px 16px" }}
        >
          <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
            {loading
              ? t("pilotConsultantTracking.detail.summary.loading")
              : t("pilotConsultantTracking.detail.summary.range", {
                  start: timeline?.rangeStart || "-",
                  end: timeline?.rangeEnd || "-",
                })}
          </Text>
        </Panel>

        {/* Timeline chart panel */}
        <Panel title={t("pilotConsultantTracking.detail.chart.title")} bodyProps={{ p: "16px" }}>
          {loading ? (
            <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
              {t("pilotConsultantTracking.detail.summary.loading")}
            </Text>
          ) : !timeline ? (
            <EmptyState
              icon={<FaUserClock />}
              title={t("pilotConsultantTracking.detail.empty")}
              hint={t("pilotConsultantTracking.detail.emptyHint", {
                defaultValue: "Adjust the date range or check back later for timeline data.",
              })}
            />
          ) : chartData.length === 0 ? (
            <EmptyState
              icon={<FaUserClock />}
              title={t("pilotConsultantTracking.detail.empty")}
            />
          ) : (
            <Stack direction="row" align="flex-end" spacing="8px" h="120px" pt="4px">
              {(() => {
                const maxValue = Math.max(...chartData.map((m) => m.value), 1);
                const maxHeight = 90;
                return chartData.map((m) => {
                  const barHeight =
                    m.value === 0 ? 2 : Math.max(10, (m.value / maxValue) * maxHeight);
                  const isSelected = m.idx === selectedMonthIndex;
                  const barBg =
                    m.value === 0 ? "portal.line" : m.isAvailable ? "green.400" : "red.400";
                  const tooltip = t("pilotConsultantTracking.detail.chart.tooltip", {
                    month: m.label,
                    count: m.value,
                    status: m.isAvailable
                      ? t("pilotConsultantTracking.list.badges.available")
                      : t("pilotConsultantTracking.list.badges.onMission"),
                    salaryReview:
                      m.hasSalaryReview && m.salaryReviewDay
                        ? t("pilotConsultantTracking.list.badges.salaryReviewDay", {
                            day: m.salaryReviewDay,
                          })
                        : m.hasSalaryReview
                        ? t("pilotConsultantTracking.list.badges.salaryReviewThisMonth")
                        : t("pilotConsultantTracking.list.badges.noSalaryReviewThisMonth"),
                  });

                  return (
                    <Box key={`${m.label}-${m.idx}`} textAlign="center" flex="1" minW="24px">
                      <Tooltip label={tooltip} placement="top" hasArrow>
                        <Box
                          bg={barBg}
                          w="100%"
                          borderRadius="6px"
                          h={`${barHeight}px`}
                          transition="height 0.3s, opacity 0.2s"
                          cursor="pointer"
                          opacity={isSelected ? 1 : 0.7}
                          outline={isSelected ? "2px solid" : "none"}
                          outlineColor={isSelected ? "portal.primary" : undefined}
                          outlineOffset="1px"
                          onClick={() => setSelectedMonthIndex(m.idx)}
                        />
                      </Tooltip>
                      <Text
                        fontSize="10px"
                        fontWeight={isSelected ? "800" : "600"}
                        mt="6px"
                        color={isSelected ? "portal.primary" : "portal.muted"}
                        whiteSpace="nowrap"
                        lineHeight="1"
                        sx={{ marginBottom: "0 !important" }}
                      >
                        {m.label}
                      </Text>
                    </Box>
                  );
                });
              })()}
            </Stack>
          )}
        </Panel>

        {/* Selected-month assignments panel */}
        <Panel
          title={
            selectedMonth
              ? t("pilotConsultantTracking.detail.chart.selectedMonth", {
                  month:
                    selectedMonth.label ||
                    selectedMonth.monthLabel ||
                    selectedMonth.month ||
                    selectedMonth.key ||
                    "-",
                })
              : t("pilotConsultantTracking.detail.assignmentsPanelTitle", "Assignments")
          }
          count={selectedMonth ? selectedAssignments.length : undefined}
          actions={
            selectedMonth ? (
              <HStack spacing="6px">
                <StatusBadge
                  label={
                    (selectedMonth.isAvailable ?? selectedMonth.is_available)
                      ? t("pilotConsultantTracking.list.badges.available")
                      : t("pilotConsultantTracking.list.badges.onMission")
                  }
                  colorScheme={
                    (selectedMonth.isAvailable ?? selectedMonth.is_available) ? "green" : "red"
                  }
                />
                {(selectedMonth.hasSalaryReview ?? selectedMonth.has_salary_review) ? (
                  <StatusBadge
                    label={t("pilotConsultantTracking.list.badges.salaryReviewThisMonth")}
                    colorScheme="purple"
                  />
                ) : null}
              </HStack>
            ) : null
          }
          bodyProps={{ p: "16px" }}
        >
          {!timeline || !selectedMonth ? (
            <EmptyState
              icon={<FaUserClock />}
              title={t("pilotConsultantTracking.detail.empty")}
            />
          ) : selectedAssignments.length === 0 ? (
            <EmptyState
              icon={<FaUserClock />}
              title={t("pilotConsultantTracking.detail.table.noAssignments")}
            />
          ) : (
            <Stack spacing="10px">
              {selectedAssignments.map((a) => {
                const matchId = a.matchId || a.match_id;
                const title = a.needTitle || a.need_title || "-";
                const client = a.clientName || a.client_name || "-";
                const status = a.status || a.assignment_status || "";
                const normalize = (v) => {
                  if (v == null) return null;
                  const s = String(v).trim();
                  return s.length ? s : null;
                };
                const start = normalize(a.start ?? a.assignment_start_date);
                const end = normalize(a.end ?? a.assignment_end_date);
                const poStatus = a.purchaseOrderStatus || a.purchase_order_status || null;
                const satScore = a.clientSatisfactionScore ?? a.client_satisfaction_score ?? null;
                const datesLabel =
                  start && end
                    ? t("pilotConsultantTracking.detail.fields.dates", { start, end })
                    : start || end || null;
                const clientId = a.clientId || a.client_id;
                const needId = a.needId || a.need_id;

                return (
                  <Box
                    key={matchId}
                    borderWidth="1px"
                    borderColor="portal.line"
                    borderRadius="10px"
                    bg="white"
                    p="12px"
                  >
                    <Flex justify="space-between" align="flex-start" gap="8px">
                      <Box minW={0} flex="1">
                        <Flex align="center" gap="8px" flexWrap="wrap">
                          <Text
                            fontSize="13px"
                            fontWeight="800"
                            color={clientId ? "portal.primary" : "portal.text"}
                            cursor={clientId ? "pointer" : "default"}
                            _hover={clientId ? { textDecoration: "underline" } : {}}
                            onClick={(e) => {
                              if (clientId) {
                                e.stopPropagation();
                                setSelectedClientId(clientId);
                                clientDetailModal.onOpen();
                              }
                            }}
                            sx={{ marginBottom: "0 !important" }}
                          >
                            {client}
                          </Text>
                          <Text
                            fontSize="13px"
                            fontWeight="600"
                            color={needId ? "portal.primary" : "portal.muted"}
                            cursor={needId ? "pointer" : "default"}
                            _hover={needId ? { textDecoration: "underline" } : {}}
                            onClick={(e) => {
                              if (needId) {
                                e.stopPropagation();
                                setSelectedNeedId(needId);
                                needDetailModal.onOpen();
                              }
                            }}
                            sx={{ marginBottom: "0 !important" }}
                          >
                            — {title}
                          </Text>
                        </Flex>
                        <HStack spacing="14px" mt="8px" flexWrap="wrap">
                          <StatusBadge
                            label={
                              getStatusLabel(
                                t,
                                MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
                                MATCH_ASSIGNMENT_STATUS_LABELS,
                                status
                              ) || status || "-"
                            }
                            colorScheme={ASSIGNMENT_STATUS_COLORS[status] || "gray"}
                          />
                          {poStatus ? (
                            <StatusBadge
                              label={t("pilotConsultantTracking.detail.badges.po", {
                                status: getStatusLabel(
                                  t,
                                  MATCH_PURCHASE_ORDER_STATUS_LABEL_KEYS,
                                  MATCH_PURCHASE_ORDER_STATUS_LABELS,
                                  poStatus
                                ),
                              })}
                              colorScheme={PURCHASE_ORDER_STATUS_COLORS[poStatus] || "gray"}
                            />
                          ) : null}
                          {satScore != null ? (
                            <StatusBadge
                              label={t("pilotConsultantTracking.detail.badges.satisfaction", {
                                score: satScore,
                              })}
                              colorScheme="purple"
                            />
                          ) : null}
                        </HStack>
                        {datesLabel ? (
                          <Text
                            fontSize="11px"
                            color="portal.muted"
                            mt="6px"
                            sx={{ marginBottom: "0 !important" }}
                          >
                            {datesLabel}
                          </Text>
                        ) : null}
                      </Box>
                      <Tooltip
                        label={t("pilotConsultantTracking.detail.actions.editTracking")}
                        placement="top"
                        hasArrow
                      >
                        <Box>
                          <MiniIconButton
                            aria-label={t("pilotConsultantTracking.detail.actions.editTracking")}
                            icon={<EditIcon />}
                            onClick={() => openTrackingModal(a)}
                          />
                        </Box>
                      </Tooltip>
                    </Flex>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Panel>
      </Box>

      <Modal isOpen={trackingModal.isOpen} onClose={closeTrackingModal} size="xl">
        <ModalOverlay />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {t("pilotConsultantTracking.detail.modal.title")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
              <FormControl>
                <FieldLabel>
                  {t("pilotConsultantTracking.detail.modal.fields.assignmentStatus")}
                </FieldLabel>
                <Select
                  value={form.assignmentStatus}
                  onChange={(e) => setForm((f) => ({ ...f, assignmentStatus: e.target.value }))}
                  sx={TOKEN_SELECT_SX}
                >
                  <option value="">{t("pilotConsultantTracking.detail.modal.fields.empty")}</option>
                  {MATCH_ASSIGNMENT_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {getStatusLabel(
                        t,
                        MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
                        MATCH_ASSIGNMENT_STATUS_LABELS,
                        status.value
                      )}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotConsultantTracking.detail.modal.fields.purchaseOrderStatus")}
                </FieldLabel>
                <Select
                  value={form.purchaseOrderStatus}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, purchaseOrderStatus: e.target.value }))
                  }
                  sx={TOKEN_SELECT_SX}
                >
                  <option value="">{t("pilotConsultantTracking.detail.modal.fields.empty")}</option>
                  {MATCH_PURCHASE_ORDER_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {getStatusLabel(
                        t,
                        MATCH_PURCHASE_ORDER_STATUS_LABEL_KEYS,
                        MATCH_PURCHASE_ORDER_STATUS_LABELS,
                        status.value
                      )}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotConsultantTracking.detail.modal.fields.assignmentStartDate")}
                </FieldLabel>
                <Input
                  type="date"
                  value={form.assignmentStartDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, assignmentStartDate: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotConsultantTracking.detail.modal.fields.assignmentEndDate")}
                </FieldLabel>
                <Input
                  type="date"
                  value={form.assignmentEndDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, assignmentEndDate: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotConsultantTracking.detail.modal.fields.purchaseOrderNumber")}
                </FieldLabel>
                <Input
                  value={form.purchaseOrderNumber}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, purchaseOrderNumber: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotConsultantTracking.detail.modal.fields.purchaseOrderReceivedAt")}
                </FieldLabel>
                <Input
                  type="datetime-local"
                  value={form.purchaseOrderReceivedAt}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, purchaseOrderReceivedAt: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotConsultantTracking.detail.modal.fields.purchaseOrderEndDate", {
                    defaultValue: "Purchase order end date",
                  })}
                </FieldLabel>
                <Input
                  type="date"
                  value={form.purchaseOrderEndDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, purchaseOrderEndDate: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotConsultantTracking.detail.modal.fields.clientSatisfactionScore")}
                </FieldLabel>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  step={1}
                  value={form.clientSatisfactionScore}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setForm((f) => ({ ...f, clientSatisfactionScore: value }));
                      return;
                    }
                    const numericValue = Number(value);
                    if (!Number.isInteger(numericValue)) return;
                    if (numericValue < 1 || numericValue > 10) return;
                    setForm((f) => ({ ...f, clientSatisfactionScore: value }));
                  }}
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl gridColumn={{ md: "1 / -1" }}>
                <FieldLabel>
                  {t("pilotConsultantTracking.detail.modal.fields.clientSatisfactionComment")}
                </FieldLabel>
                <Textarea
                  value={form.clientSatisfactionComment}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, clientSatisfactionComment: e.target.value }))
                  }
                  rows={3}
                  fontSize="13px"
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={closeTrackingModal}>
              {t("pilotConsultantTracking.detail.modal.actions.cancel")}
            </Button>
            <Button variant="primary" size="sm" onClick={handleSaveTracking} isLoading={saving}>
              {t("pilotConsultantTracking.detail.modal.actions.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {selectedClientId && (
        <ClientDetailModal
          isOpen={clientDetailModal.isOpen}
          onClose={() => {
            clientDetailModal.onClose();
            setSelectedClientId(null);
          }}
          clientId={selectedClientId}
        />
      )}

      {selectedNeedId && (
        <NeedDetailModal
          isOpen={needDetailModal.isOpen}
          onClose={() => {
            needDetailModal.onClose();
            setSelectedNeedId(null);
          }}
          needId={selectedNeedId}
          shouldNavigateOnClose={false}
        />
      )}
    </Box>
  );
};

export default ConsultantTrackingDetail;
